import { inject, Service } from '@angular/core';
import {
  CheckAstatDailyGuestsInClosurePeriodRequestDtoInput,
  GetAstatDailyLocalTaxInfoRequestDtoInput,
  GetCityTaxCheckInformationRequestDtoInput,
  GetIstatCustomerRequestDtoInput,
  SendAstatDailyFacilityClosureRequestDtoInput,
  SendAstatDailyIstatClosureRequestDtoInput,
  SendIstatRequestDtoInput,
  UpsertCustomerDtoInput,
} from '@h5s/dtos-api';
import {
  AstatDailyLocalTaxInfo,
  AstatDailyLocalTaxInfoRequest,
  CheckAstatDailyGuestsInClosurePeriodGql,
  CheckAstatDailyGuestsInClosurePeriodQueryResult,
  CheckAstatDailyGuestsInClosurePeriodQueryVariables,
  CheckAstatDailyGuestsInClosurePeriodRequest,
  CheckAstatDailyGuestsInClosurePeriodResponse,
  CityTaxCheckInformation,
  Customer,
  GetAllOwnerIstatGql,
  GetAllOwnerIstatQueryResult,
  GetAstatDailyLocalTaxInfoGql,
  GetAstatDailyLocalTaxInfoQueryResult,
  GetAstatDailyLocalTaxInfoQueryVariables,
  GetCityTaxCheckInformationGql,
  GetCityTaxCheckInformationQueryResult,
  GetCityTaxCheckInformationQueryVariables,
  GetCityTaxCheckInformationRequest,
  GetIstatCustomerGql,
  GetIstatCustomerQueryResult,
  GetIstatCustomerQueryVariables,
  GetIstatCustomerRequest,
  GetIstatGenerationParametersGql,
  GetIstatGenerationParametersQueryResult,
  GetIstatGenerationParametersQueryVariables,
  IstatCustomer,
  IstatGenerationParameters,
  OwnerIstatProfile,
  ResourceValidationResult,
  SendAstatDailyFacilityClosureGql,
  SendAstatDailyFacilityClosureMutationResult,
  SendAstatDailyFacilityClosureMutationVariables,
  SendAstatDailyFacilityClosureRequest,
  SendAstatDailyIstatClosureGql,
  SendAstatDailyIstatClosureMutationResult,
  SendAstatDailyIstatClosureMutationVariables,
  SendAstatDailyIstatClosureRequest,
  SendIstatGql,
  SendIstatMutationResult,
  SendIstatMutationVariables,
  SendIstatRequest,
  UpdateCustomer,
  ValidateCustomerForIstatGql,
  ValidateCustomerForIstatQueryResult,
  ValidateCustomerForIstatQueryVariables,
} from '@h5s/entities';
import { getValueOrError, throwErrorIfNullFromGraphQL } from '@h5s/util-error';
import { mapArrayResponse, mapParamDto, mapQueryParamDto, mapResponse } from '@h5s/util-object';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';

@Service()
export class DataAccessIstatService {
  private readonly apollo = inject(Apollo);

  /**
   * Ottiene la lista di OwnerIstatProfile
   */
  public getOwnerIstatProfile(): Observable<OwnerIstatProfile[]> {
    return this.apollo
      .query<GetAllOwnerIstatQueryResult>({
        query: GetAllOwnerIstatGql,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(OwnerIstatProfile, result.getAllOwnerIstat)),
      );
  }

  /**
   * Ottiene la lista di IstatCustomer
   */
  public getIstatCustomer(request: GetIstatCustomerRequest): Observable<IstatCustomer[]> {
    const getIstatCustomerRequestDTO = mapQueryParamDto<GetIstatCustomerRequestDtoInput>(request);

    const variables: GetIstatCustomerQueryVariables = {
      getIstatCustomerRequestDTO: getIstatCustomerRequestDTO,
    };

    return this.apollo
      .query<GetIstatCustomerQueryResult>({
        query: GetIstatCustomerGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(IstatCustomer, result.getIstatCustomer)),
      );
  }

  /**
   * Controlla se ci sono ospiti dichiarati nei giorni di chiusura passati come parametri
   */
  public checkAstatDailyGuestsInClosurePeriod(
    paramsRequest: CheckAstatDailyGuestsInClosurePeriodRequest,
  ): Observable<CheckAstatDailyGuestsInClosurePeriodResponse> {
    const checkAstatDailyGuestsInClosurePeriodRequestDTO =
      mapQueryParamDto<CheckAstatDailyGuestsInClosurePeriodRequestDtoInput>(paramsRequest);

    const variables: CheckAstatDailyGuestsInClosurePeriodQueryVariables = {
      checkAstatDailyGuestsInClosurePeriodRequestDTO: checkAstatDailyGuestsInClosurePeriodRequestDTO,
    };

    return this.apollo
      .query<CheckAstatDailyGuestsInClosurePeriodQueryResult>({
        query: CheckAstatDailyGuestsInClosurePeriodGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) =>
          mapResponse(CheckAstatDailyGuestsInClosurePeriodResponse, result.checkAstatDailyGuestsInClosurePeriod),
        ),
      );
  }

  /**
   * Invia i dati dei giorni di chiusura
   */
  public sendAstatDailyFacilityClosure(paramsRequest: SendAstatDailyFacilityClosureRequest): Observable<string> {
    const sendAstatDailyFacilityClosureRequestDTO =
      mapQueryParamDto<SendAstatDailyFacilityClosureRequestDtoInput>(paramsRequest);

    const variables: SendAstatDailyFacilityClosureMutationVariables = {
      sendAstatDailyFacilityClosureRequestDTO: sendAstatDailyFacilityClosureRequestDTO,
    };

    return this.apollo
      .mutate<SendAstatDailyFacilityClosureMutationResult>({
        mutation: SendAstatDailyFacilityClosureGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => getValueOrError(result.sendAstatDailyFacilityClosure)),
      );
  }

  /**
   * Ottiene i dati per consolidare da ASTAT
   */
  public getAstatDailyLocalTaxInfo(paramsRequest: AstatDailyLocalTaxInfoRequest): Observable<AstatDailyLocalTaxInfo> {
    const getAstatDailyLocalTaxInfoRequestDTO =
      mapQueryParamDto<GetAstatDailyLocalTaxInfoRequestDtoInput>(paramsRequest);

    const variables: GetAstatDailyLocalTaxInfoQueryVariables = {
      getAstatDailyLocalTaxInfoRequestDTO: getAstatDailyLocalTaxInfoRequestDTO,
    };

    return this.apollo
      .query<GetAstatDailyLocalTaxInfoQueryResult>({
        query: GetAstatDailyLocalTaxInfoGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(AstatDailyLocalTaxInfo, result.getAstatDailyLocalTaxInfo)),
      );
  }

  /**
   * Invia i dati per consolidare ASTAT
   */
  public sendAstatDailyIstatClosure(paramsRequest: SendAstatDailyIstatClosureRequest): Observable<string> {
    const sendAstatDailyIstatClosureRequestDTO =
      mapQueryParamDto<SendAstatDailyIstatClosureRequestDtoInput>(paramsRequest);

    const variables: SendAstatDailyIstatClosureMutationVariables = {
      sendAstatDailyIstatClosureRequestDTO: sendAstatDailyIstatClosureRequestDTO,
    };

    return this.apollo
      .mutate<SendAstatDailyIstatClosureMutationResult>({
        mutation: SendAstatDailyIstatClosureGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => getValueOrError(result.sendAstatDailyIstatClosure)),
      );
  }

  /**
   * Ottiene IstatGenerationParameters dato l'id dell OwnerIstatProfile selezionato
   */
  public getIstatGenerationParameters(ownerIstatProfileId: number): Observable<IstatGenerationParameters> {
    const variables: GetIstatGenerationParametersQueryVariables = {
      ownerIstatId: ownerIstatProfileId,
    };

    return this.apollo
      .query<GetIstatGenerationParametersQueryResult>({
        query: GetIstatGenerationParametersGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(IstatGenerationParameters, result.getIstatGenerationParameters)),
      );
  }

  /**
   * Validate customer for ISTAT
   *
   * @param {UpdateCustomer} customer
   * @returns {Observable<ResourceValidationResult<Customer>[]>}
   */
  public validateCustomerForIstat(customer: UpdateCustomer): Observable<ResourceValidationResult<Customer>[]> {
    const upsertCustomerDTO = mapParamDto<UpsertCustomerDtoInput>(customer);

    const variables: ValidateCustomerForIstatQueryVariables = {
      upsertCustomerDTO,
    };

    return this.apollo
      .query<ValidateCustomerForIstatQueryResult>({
        query: ValidateCustomerForIstatGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapArrayResponse(ResourceValidationResult<Customer>, result.validateCustomerForIstat)),
      );
  }

  /**
   * Invia Online Istat
   */
  public sendIstatOnline(request: SendIstatRequest): Observable<string> {
    const sendIstatRequestDTO = mapParamDto<SendIstatRequestDtoInput>(request);

    const variables: SendIstatMutationVariables = {
      sendIstatRequestDTO: sendIstatRequestDTO,
    };

    return this.apollo
      .mutate<SendIstatMutationResult>({
        mutation: SendIstatGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => result.sendIstat ?? ''),
      );
  }

  /**
   * Ottiene le informazioni per il controllo della tassa di soggiorno
   */
  public getCityTaxCheckInformation(request: GetCityTaxCheckInformationRequest): Observable<CityTaxCheckInformation> {
    const cityTaxCheckInformationRequestDTO = mapParamDto<GetCityTaxCheckInformationRequestDtoInput>(request);
    const variables: GetCityTaxCheckInformationQueryVariables = {
      cityTaxCheckInformationRequestDTO: cityTaxCheckInformationRequestDTO,
    };

    return this.apollo
      .query<GetCityTaxCheckInformationQueryResult>({
        query: GetCityTaxCheckInformationGql,
        variables: variables,
      })
      .pipe(
        throwErrorIfNullFromGraphQL((data) => data),
        map((result) => mapResponse(CityTaxCheckInformation, result.getCityTaxCheckInformation)),
      );
  }
}
